import React, { useCallback, useEffect, useState } from 'react';
import useEmblaCarousel from 'embla-carousel-react';
import { prefersReducedMotion } from '@/lib/prerender';

const AUTOPLAY_MS = 7000;

/**
 * Testimonial carousel — used when the sheet provides more than two testimonials.
 * Two cards per view on desktop, one on mobile; loop; prev/next + dots; gentle
 * autoplay that pauses on hover/focus and is disabled for reduced-motion users.
 */
export default function TestimonialCarousel({ items, renderItem }) {
  const reduced = prefersReducedMotion();
  const [emblaRef, embla] = useEmblaCarousel({ loop: true, align: 'start', slidesToScroll: 1, skipSnaps: false });
  const [selected, setSelected] = useState(0);
  const [snaps, setSnaps] = useState([]);
  const [paused, setPaused] = useState(false);

  const onSelect = useCallback(() => { if (embla) setSelected(embla.selectedScrollSnap()); }, [embla]);

  useEffect(() => {
    if (!embla) return undefined;
    setSnaps(embla.scrollSnapList());
    onSelect();
    embla.on('select', onSelect);
    embla.on('reInit', () => { setSnaps(embla.scrollSnapList()); onSelect(); });
    return () => { embla.off('select', onSelect); };
  }, [embla, onSelect]);

  useEffect(() => {
    if (!embla || reduced || paused) return undefined;
    const id = setInterval(() => embla.scrollNext(), AUTOPLAY_MS);
    return () => clearInterval(id);
  }, [embla, reduced, paused]);

  const btn = 'w-11 h-11 border border-white/20 text-white hover:border-[#4A90E2] hover:text-[#4A90E2] transition-colors rounded-[2px] font-mono text-lg flex items-center justify-center focus-visible:outline-none focus-visible:border-[#4A90E2]';

  return (
    <div
      className="relative"
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
      onFocusCapture={() => setPaused(true)}
      onBlurCapture={() => setPaused(false)}
      data-testid="testimonial-carousel"
      role="region"
      aria-roledescription="carousel"
      aria-label="Client testimonials"
    >
      <div className="overflow-hidden" ref={emblaRef}>
        <div className="flex -ml-8">
          {items.map((item, i) => (
            <div
              key={item.id}
              className="min-w-0 flex-[0_0_100%] md:flex-[0_0_50%] pl-8"
              role="group"
              aria-roledescription="slide"
              aria-label={`${i + 1} of ${items.length}`}
              data-testid={`testimonial-slide-${i}`}
            >
              {renderItem(item)}
            </div>
          ))}
        </div>
      </div>

      <div className="mt-8 flex items-center justify-between">
        <div className="flex items-center gap-2" role="tablist" aria-label="Choose testimonial">
          {snaps.map((_, i) => (
            <button
              key={i}
              type="button"
              role="tab"
              aria-selected={i === selected}
              aria-label={`Go to testimonial ${i + 1}`}
              onClick={() => embla?.scrollTo(i)}
              className="h-3 px-1 group focus-visible:outline-none"
              data-testid={`testimonial-dot-${i}`}
            >
              <span
                className="block h-px transition-all duration-300"
                style={{ width: i === selected ? 28 : 12, background: i === selected ? '#4A90E2' : 'rgba(255,255,255,0.3)' }}
              />
            </button>
          ))}
        </div>
        <div className="flex items-center gap-3">
          <button type="button" className={btn} onClick={() => embla?.scrollPrev()} aria-label="Previous testimonial" data-testid="testimonial-prev">←</button>
          <button type="button" className={btn} onClick={() => embla?.scrollNext()} aria-label="Next testimonial" data-testid="testimonial-next">→</button>
        </div>
      </div>
    </div>
  );
}
